import React, { useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react'
import { useNewTranslation, useWindowSize } from 'hooks'
import { useCheckSomePermissions, usePermission, useCheckEditTemplatedCampaignPermission } from 'hooks/permissions'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import NumberInput from 'particles/NumberInput'
import { useDispatch, useSelector } from 'react-redux'
import { selectCountrySetting } from 'state/constants'
import {
    CampaignTemplateStep,
    selectPaymentSummary,
    selectTemplateDetail,
    updatePaymentSummary,
} from 'state/campaignTemplates'
import { COUNTRY_ENUM } from 'services/api/constants'
import IconButton from 'particles/Button/IconButton'
import RectangleButton from 'particles/Button/RectangleButton'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import { debounce } from 'lodash'
import qs from 'query-string'
import Loading from 'components/Loading'
import { selectCampaignByTemplateData, selectProfile, selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { TFunction } from 'i18next'
import { ElementKeys } from '../TemplateDetail/options'
import { formatCurrencyNumberInput } from 'components/utils'
import { fieldIdentifiers } from 'components/Shared/Comment'
import TemplateInputFieldContainer from '../../../Shared/Permission/TemplateInputFieldContainer'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import { CAMPAIGN_STATUS_ENUM, CampaignState } from 'state/campaigns'
import { getTasksList } from 'state/tasks'
import PaymentBanner from './PaymentBanner'
import ExportToggle from './ExportToggle'
import BannerAlert from './BannerAlert'
import Icon from 'particles/Icon'
import { AdditionalFeeProps } from 'components/Campaign/common/Modals/UpdateAdditionalFee'
import { selectIsInternalUser } from 'state/profile'
import Restricted from 'components/Shared/Permission/Restricted'
import { MODAL_TYPE, PredefinedModal } from 'components/Campaign/common/Modals'
import { CampaignPaymentSummaryData } from 'services/api/campaigns'
import { selectTeamFilter } from 'state/filter'
import { NewTrans } from 'hooks/index'
interface ModalState {
    type?: MODAL_TYPE
    additionalFee?: AdditionalFeeProps
    serviceFee?: number
    campaign: CampaignState | null
    paymentSummary?: CampaignPaymentSummaryData
    isTemplate: boolean
    tax?: number
}
interface PaymentConfirmationProps {
    onSubmit: (isExport: boolean) => void
    onBack: () => void
    setShowPaymentModal: Dispatch<SetStateAction<boolean>>
}

const getBreakdownTitle = (t: TFunction): Record<ElementKeys, string> => ({
    purchase: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.purchaseTitle'),
    ig_post: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.instagramPostTask.title'),
    }),
    download_app: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.downloadAppTask.title'),
    }),
    download_app_sign_up_without_id: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.downloadRegisterAppTask.title'),
    }),
    ig_reels: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.instagramReelTask.title'),
    }),
    ig_story_non_ugc_sticker_link: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.instagramStoryNonUGC.title'),
    }),
    ig_story_repost: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.instagramStoryRepostTask.title'),
    }),
    review_text_image_approval: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.reviewTextImageWithApprovalTask.title'),
    }),
    review_text_image_approval_purchase: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.reviewTextImageWithApprovalTask.title'),
    }),
    tiktok: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.tiktokTask.title'),
    }),
    tiktok_repost: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.tiktokRepostTask.title'),
    }),
    ig_story_ugc: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.instagramStoryTask.title'),
    }),
    review_text: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.reviewTextTask.title'),
    }),
    review_text_purchase: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.reviewTextTask.title'),
    }),
    review_text_approval: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.reviewTextWithApprovalTask.title'),
    }),
    follow: t('campaign.template.step3.paymentConfirmation.payoutBreakdown.taskTitle', {
        taskTitle: t('campaign.template.step2.followTask.title'),
    }),
    video_creation: t('campaign.template.step2.videoCreationTask.title'),
    photo_creation: t('campaign.template.step2.photoCreationTask.title'),
})

const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({ onSubmit, onBack, setShowPaymentModal }) => {
    const [t] = useNewTranslation()
    const campaign = useSelector(selectCampaignByTemplateData)
    const templateDetail = useSelector(selectTemplateDetail)
    const countrySetting = useSelector(selectCountrySetting((templateDetail?.country as COUNTRY_ENUM) || null))
    const minimumBudget = countrySetting?.minimum_budget_for_template ?? 0
    const paymentSummary = useSelector(selectPaymentSummary)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetPaymentSummary]))
    const [isExpanded, setIsExpanded] = useState({
        influencerBudget: false,
        partipostFee: false,
        additionalFees: false,
    })
    const [isExport, setIsExport] = useState(false)
    const [openModal, setOpenModal] = useState<ModalState>({
        campaign: campaign,
        additionalFee: undefined,
        serviceFee: undefined,
        type: undefined,
        paymentSummary: paymentSummary,
        isTemplate: true,
        tax: undefined,
    })
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const [budget, setBudget] = useState<string | null>(null)
    const breakdownTitleMap = getBreakdownTitle(t)
    const windowSize = useWindowSize()
    const isTabletSize = windowSize <= 768
    const isPhoneSize = windowSize <= 375
    const ableToPay =
        campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT &&
        useCheckSomePermissions([CampaignStatusControl.PayCampaign])
    const dispatch = useDispatch()
    const isCampaignForReview = campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isCampaignForEdit = campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_EDIT
    const isCampaignWaitingForPayment = campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT
    const isCampaignReadyForLaunch = campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    const isInternalUser = useSelector(selectIsInternalUser)
    const teamFilter = useSelector(selectTeamFilter)
    const canLaunchCampaign = usePermission(CampaignStatusControl.LaunchCampaign)
    const canUpdateFees =
        isCampaignForEdit || isCampaignForReview
            ? isInternalUser && canLaunchCampaign
            : isCampaignWaitingForPayment || isCampaignLive || isCampaignReadyForLaunch
              ? false
              : isInternalUser
    const profileDetail = useSelector(selectProfile)
    const templatePath = location.pathname + location.search
    const showPaymentTotal = isInternalUser
        ? campaign?.has_budget
        : campaign?.has_budget && usePermission(CampaignStatusControl.PayCampaign)

    const isPermissionEditable = useCheckEditTemplatedCampaignPermission(
        (campaign && campaign.status) || undefined,
        false,
    )

    const debounceBudgetUpdate = debounce(() => {
        if (budget === null) return
        if (typeof templateDataUuid === 'string') {
            // prevent negative number
            const payloadBudget = +budget > 0 ? +budget : 0
            if (!Number.isNaN(payloadBudget)) {
                dispatch(
                    updatePaymentSummary({
                        templateUuid: templateDataUuid,
                        budget: payloadBudget,
                    }),
                )
            }
        }
    }, 500)

    useEffect(() => {
        if (isCampaignForReview && canLaunchCampaign)
            setIsExpanded({
                ...isExpanded,
                influencerBudget: true,
                partipostFee: true,
                additionalFees: true,
            })
    }, [isCampaignForReview, canLaunchCampaign])

    useEffect(() => {
        if (paymentSummary?.budget && budget === null) {
            setBudget(paymentSummary.budget?.toString())
        }
    }, [paymentSummary?.budget])

    useEffect(() => {
        if (budget === null) return
        if (
            campaign?.status !== CAMPAIGN_STATUS_ENUM.LIVE &&
            budget !== '' &&
            paymentSummary?.budget !== +budget &&
            paymentSummary?.budget !== undefined &&
            !budgetInputError
        ) {
            debounceBudgetUpdate()
        }

        return () => debounceBudgetUpdate.cancel()
    }, [budget, paymentSummary?.budget, debounceBudgetUpdate])

    useEffect(() => {
        if (campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE) {
            dispatch(
                getTasksList({
                    campaignUuid: campaign.uuid,
                }),
            )
        }
    }, [campaign?.status])

    const budgetCurrencySign = useMemo(() => {
        if (!countrySetting) return null
        return countrySetting.currency.sign
    }, [countrySetting, templateDetail?.country])

    const paymentAmountDisplay = formatCurrencyNumberInput({
        value: paymentSummary?.payable_amount ?? 0,
        country: templateDetail?.country as COUNTRY_ENUM,
        currencySign: budgetCurrencySign,
        decimal: true,
    })

    const formatCurrencyValue = (value: number, isDecimal = true) => {
        return formatCurrencyNumberInput({
            value: value || 0,
            country: templateDetail?.country as COUNTRY_ENUM,
            currencySign: budgetCurrencySign,
            decimal: isDecimal,
        })
    }

    const totalPartiposterBasePayout = useMemo(() => {
        if (!paymentSummary) return 0
        return paymentSummary?.total_payout * paymentSummary?.slot_number
    }, [paymentSummary?.total_payout, paymentSummary?.slot_number])

    const onChangeBudget = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        if (Number(value) < 0) {
            setBudget('0')
        } else {
            setBudget(value)
        }
    }

    const totalAdjustments = useMemo(() => {
        return paymentSummary?.additional_fees?.reduce((sum, item) => sum + item.amount, 0)
    }, [paymentSummary?.additional_fees])

    const budgetInputError = useMemo(() => {
        if (budget === null) return
        return !budget
            ? t('errorMessage.validation.enterCampaignBudget')
            : Number(budget) < minimumBudget
              ? t('campaign.template.yourBudget.errorLowerthanMinimumInfluencerBudget', { minimumBudget })
              : ''
    }, [budget])

    const breakdownItem = useMemo(() => {
        return (
            <div className="accordion-content">
                <div className="accordion-header">
                    <div>
                        <Text scale="bodyS500">
                            {t(
                                'campaign.template.step3.payoutBreakdown.influencerBudgetItem.totalPartiposterBasePayout.title',
                            )}
                        </Text>
                        <Text scale="caption400">
                            {t(
                                'campaign.template.step3.payoutBreakdown.influencerBudgetItem.totalPartiposterBasePayout.subtext',
                            )}
                        </Text>
                    </div>
                    <Text scale="bodyS500" color="secondary" className="breakdown-payout">
                        {`${formatCurrencyValue(paymentSummary?.total_payout || 0)} x ${paymentSummary?.slot_number || 0} = `}
                        {formatCurrencyValue(totalPartiposterBasePayout || 0)}
                    </Text>
                </div>
                <div className="divider" />
                <Text scale="caption400">
                    {t('campaign.template.step3.payoutBreakdown.influencerBudgetItem.perPartipostPayout')}
                </Text>
                {paymentSummary?.breakdown?.map((breakdown, idx) => (
                    <>
                        <BreakdownItem>
                            <div className="breakdown-info padded-list">
                                <Text scale="bodyS500" className="breakdown-task-title">
                                    {breakdownTitleMap[breakdown.element_key]}
                                </Text>
                                {breakdown.element_key === 'purchase' && (
                                    <Text scale="caption400" color="secondary">
                                        {t(
                                            'campaign.template.step3.paymentConfirmation.payoutBreakdown.purchaseSubText',
                                            {
                                                shippingFee: formatCurrencyValue(breakdown.shipping_fee || 0),
                                                numberOfItem: breakdown.min_purchase_quantity,
                                                count: breakdown.min_purchase_quantity || 1, // 0 will return singular
                                            },
                                        )}
                                    </Text>
                                )}
                            </div>
                            <Text scale="bodyS500" color="secondary" className="breakdown-payout">
                                {formatCurrencyValue(breakdown.payout || 0)}
                            </Text>
                        </BreakdownItem>
                        {paymentSummary?.breakdown && idx === paymentSummary.breakdown.length - 1 && (
                            <div className="divider" />
                        )}
                    </>
                ))}
            </div>
        )
    }, [paymentSummary?.breakdown, budgetCurrencySign, templateDetail?.country, t])

    const serviceFeeBreakdownItem = useMemo(() => {
        return (
            <div className="accordion-content">
                <BreakdownItem>
                    <div className="breakdown-info">
                        <Text scale="bodyS500" className="breakdown-task-title">
                            {t('campaign.template.step3.payoutBreakdown.partipostFee.title')}
                        </Text>
                        <Text scale="caption400" color="secondary">
                            {t('campaign.template.step3.payoutBreakdown.partipostFee.subtext', {
                                partipostFee: paymentSummary?.service_fee_percentage || ' - ',
                            })}
                        </Text>
                    </div>
                    <div className="breakdown-additional">
                        <Text scale="bodyS500" color="secondary" className="breakdown-payout">
                            {formatCurrencyValue(paymentSummary?.partipost_fee || 0)}
                        </Text>
                        {canUpdateFees && (
                            <IconButton
                                icon="pencilLine"
                                size="xs"
                                form="icon"
                                color="neutral"
                                onClick={() =>
                                    setOpenModal({
                                        ...openModal,
                                        campaign,
                                        serviceFee: paymentSummary?.service_fee_percentage,
                                        type: MODAL_TYPE.EDIT_SERVICE_FEE,
                                    })
                                }
                            />
                        )}
                    </div>
                </BreakdownItem>
            </div>
        )
    }, [paymentSummary?.partipost_fee, budgetCurrencySign, templateDetail?.country, t])

    const additionalFeesBreakdownItem = useMemo(() => {
        return (
            <div className="accordion-content">
                {paymentSummary?.additional_fees?.map((fee, idx) => (
                    <BreakdownItem key={idx}>
                        <div className="breakdown-info">
                            <Text scale="bodyS500" className="breakdown-task-title">
                                {fee.title}
                            </Text>
                        </div>
                        <div className="breakdown-additional">
                            <Text scale="bodyS500" color="secondary" className="breakdown-payout">
                                {formatCurrencyValue(fee.amount || 0)}
                            </Text>
                            {canUpdateFees && (
                                <>
                                    <IconButton
                                        icon="pencilLine"
                                        size="xs"
                                        form="icon"
                                        color="neutral"
                                        onClick={() => {
                                            setOpenModal({
                                                ...openModal,
                                                campaign,
                                                additionalFee: fee,
                                                type: MODAL_TYPE.EDIT_PAYMENT_ADJUSTMENT,
                                                paymentSummary: paymentSummary,
                                            })
                                        }}
                                    />
                                    <IconButton
                                        icon="x"
                                        size="xs"
                                        form="icon"
                                        color="neutral"
                                        onClick={() =>
                                            setOpenModal({
                                                ...openModal,
                                                campaign,
                                                additionalFee: fee,
                                                type: MODAL_TYPE.DELETE_PAYMENT_ADJUSTMENT,
                                                paymentSummary: paymentSummary,
                                            })
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </BreakdownItem>
                ))}
                {canUpdateFees && (
                    <RectangleButton
                        label={t('campaign.template.step3.payoutBreakdown.addPaymentAdjustButton')}
                        icon="plus"
                        form="outlined"
                        color="primary"
                        size="s"
                        style={{ width: 'fit-content' }}
                        onClick={() =>
                            setOpenModal({
                                ...openModal,
                                campaign,
                                additionalFee: undefined,
                                type: MODAL_TYPE.ADD_PAYMENT_ADJUSTMENT,
                                paymentSummary: paymentSummary,
                            })
                        }
                    />
                )}
            </div>
        )
    }, [paymentSummary?.additional_fees, budgetCurrencySign, templateDetail?.country, t])

    const explanationText =
        campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT
            ? t('campaign.template.step3.helpText.waitingForPayment')
            : campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH
              ? t('campaign.template.step3.helpText.waitingForLaunch')
              : ''
    const explanationTextColor = campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH ? 'positive' : 'secondary'

    return (
        <PaymentConfirmationWrapper>
            <div className="payment-confirmation-title">
                <Text scale={isTabletSize ? 'H4' : 'H3'}>{t('campaign.template.step3.paymentConfirmation.title')}</Text>
                <Text scale="bodyM400" color="secondary">
                    {t('campaign.template.step3.paymentConfirmation.secondarySubtext')}
                </Text>
            </div>

            <PaymentBanner />

            <ExportToggle isExport={isExport} setIsExport={setIsExport} />

            <div className="summary-content-wrapper">
                {isLoading ? (
                    <Loading />
                ) : paymentSummary ? (
                    <div className="summary-content">
                        <div className="template-info">
                            <Text scale="bodyM700" color="secondary">
                                {t(paymentSummary?.template_name || '')}
                            </Text>
                            <div className="estimation">
                                <Text scale="H4">{paymentSummary?.campaign_title}</Text>
                                <Text color="info" scale="H4">
                                    {t('campaign.template.step3.paymentConfirmation.payoutBreakdown.estimationNumber', {
                                        estimationNumber: paymentSummary?.slot_number,
                                        count: paymentSummary?.slot_number || 0,
                                    })}
                                </Text>
                            </div>
                        </div>
                        <TemplateInputFieldContainer>
                            <NumberInput
                                isRequired
                                size="m"
                                labelSize="l"
                                label={t('campaign.template.yourBudget.title')}
                                subtext={isPermissionEditable ? t('campaign.template.yourBudget.subtext') : ''}
                                width="100%"
                                prefix={budgetCurrencySign}
                                value={budget?.toString()}
                                min={0}
                                onChange={onChangeBudget}
                                error={budgetInputError}
                                isReadOnly={campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH || undefined}
                                placeholder="0.00"
                                identifier={`${CampaignTemplateStep.PAYMENT_CONFIRMATION}_${fieldIdentifiers.templateBudget}`}
                                data-testid="test-campaign-budget"
                            />
                        </TemplateInputFieldContainer>
                        <Restricted
                            permissions={[CampaignStatusControl.LaunchCampaign, CampaignStatusControl.PayCampaign]}
                        >
                            <div className="breakdown-container">
                                {!isExport && <BannerAlert paymentSummary={paymentSummary} />}
                                {isExport && (
                                    <ReminderAlert
                                        form="warning"
                                        title={t(
                                            'campaign.template.step3.paymentConfirmation.payoutBreakdown.warningReminder.editorSwitchOn.title',
                                        )}
                                        subtext={t(
                                            'campaign.template.step3.paymentConfirmation.payoutBreakdown.warningReminder.editorSwitchOn.subtext',
                                        )}
                                        width="100%"
                                    />
                                )}

                                {paymentSummary?.detailed_mode && !isExport && campaign?.has_budget ? (
                                    <>
                                        <div className="breakdown-section">
                                            <div className="flex-between per-partiposter-payout">
                                                <BreakdownTitle scale="bodyM500">
                                                    {t(
                                                        'campaign.template.step3.payoutBreakdown.influencerBudget.title',
                                                    )}
                                                    <IconButton
                                                        form="icon"
                                                        size="xs"
                                                        icon={isExpanded.influencerBudget ? 'caretUp' : 'caretDown'}
                                                        color="neutral"
                                                        onClick={() =>
                                                            setIsExpanded(prevState => ({
                                                                ...prevState,
                                                                influencerBudget: !prevState.influencerBudget,
                                                            }))
                                                        }
                                                    />
                                                </BreakdownTitle>
                                                <Text scale="bodyM700">
                                                    {formatCurrencyValue(paymentSummary?.influencer_budget || 0)}
                                                </Text>
                                            </div>

                                            {isExpanded.influencerBudget && !isExport && breakdownItem}
                                        </div>

                                        <div className="breakdown-section">
                                            <div className="flex-between per-partiposter-payout">
                                                <BreakdownTitle scale="bodyM500">
                                                    {t(
                                                        'campaign.template.step3.payoutBreakdown.partipostFeeItem.title',
                                                    )}
                                                    <IconButton
                                                        form="icon"
                                                        size="xs"
                                                        icon={isExpanded.partipostFee ? 'caretUp' : 'caretDown'}
                                                        color="neutral"
                                                        onClick={() =>
                                                            setIsExpanded(prevState => ({
                                                                ...prevState,
                                                                partipostFee: !prevState.partipostFee,
                                                            }))
                                                        }
                                                    />
                                                </BreakdownTitle>
                                                <Text scale="bodyM700">
                                                    {formatCurrencyValue(paymentSummary?.partipost_fee || 0)}
                                                </Text>
                                            </div>

                                            {isExpanded.partipostFee && !isExport && serviceFeeBreakdownItem}
                                        </div>

                                        <div className="breakdown-section">
                                            <div className="flex-between per-partiposter-payout">
                                                <BreakdownTitle scale="bodyM500">
                                                    {t(
                                                        'campaign.template.step3.payoutBreakdown.additonalService.title',
                                                    )}
                                                    {canUpdateFees ||
                                                    (paymentSummary?.additional_fees &&
                                                        paymentSummary?.additional_fees?.length > 0) ? (
                                                        <IconButton
                                                            form="icon"
                                                            size="xs"
                                                            icon={isExpanded.additionalFees ? 'caretUp' : 'caretDown'}
                                                            color="neutral"
                                                            onClick={() =>
                                                                setIsExpanded(prevState => ({
                                                                    ...prevState,
                                                                    additionalFees: !prevState.additionalFees,
                                                                }))
                                                            }
                                                        />
                                                    ) : null}
                                                </BreakdownTitle>
                                                <Text scale="bodyM700" style={{ flex: 'none' }}>
                                                    {formatCurrencyValue(totalAdjustments || 0)}
                                                </Text>
                                            </div>

                                            {isExpanded.additionalFees && !isExport && additionalFeesBreakdownItem}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </Restricted>

                        {!isExport && campaign?.has_budget ? <div className="divider" id="maindiv" /> : null}

                        {!isExport && showPaymentTotal ? (
                            <div className="payable-amount">
                                <div className="payable-amount-breakdown">
                                    <Text
                                        scale="bodyM500"
                                        style={{ marginRight: 24 }}
                                        color={paymentSummary && paymentSummary?.subtotal < 0 ? 'error' : 'primary'}
                                    >
                                        {t('campaign.template.step3.payoutBreakdown.subtotal.title')}
                                    </Text>
                                    <Text
                                        scale="bodyM700"
                                        style={{ minWidth: 140, textAlign: 'right' }}
                                        color={paymentSummary && paymentSummary?.subtotal < 0 ? 'error' : 'primary'}
                                    >
                                        {formatCurrencyValue(paymentSummary?.subtotal ?? 0)}
                                    </Text>
                                </div>
                                {campaign?.country !== COUNTRY_ENUM.HK && (
                                    <div className="payable-amount-breakdown">
                                        <div className="tax-label">
                                            <Text
                                                scale="bodyM500"
                                                style={{ ...(!canUpdateFees ? { marginRight: 24 } : {}) }}
                                                color={
                                                    paymentSummary && paymentSummary?.tax.amount < 0
                                                        ? 'error'
                                                        : 'primary'
                                                }
                                            >
                                                {`${paymentSummary?.tax.label} ${paymentSummary?.tax.percentage}%`}
                                            </Text>
                                            {canUpdateFees && (
                                                <IconButton
                                                    icon="pencilLine"
                                                    size="xs"
                                                    form="icon"
                                                    color="neutral"
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        setOpenModal({
                                                            ...openModal,
                                                            campaign,
                                                            tax: paymentSummary?.tax.percentage,
                                                            type: MODAL_TYPE.UPDATE_TAX,
                                                        })
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <Text
                                            scale="bodyM700"
                                            style={{ minWidth: 140, textAlign: 'right' }}
                                            color={
                                                paymentSummary && paymentSummary?.tax.amount < 0 ? 'error' : 'primary'
                                            }
                                        >
                                            {formatCurrencyValue(paymentSummary?.tax.amount ?? 0)}
                                        </Text>
                                    </div>
                                )}
                                <div className="payable-amount-breakdown" style={{ marginTop: 4 }}>
                                    <Text
                                        scale="H4"
                                        color={
                                            paymentSummary && paymentSummary?.payable_amount < 0 ? 'error' : 'primary'
                                        }
                                        style={{ marginRight: 24 }}
                                    >
                                        {t('campaign.template.step3.payoutBreakdown.campaignPayment.title')}
                                    </Text>
                                    <Text
                                        scale="H4"
                                        color={
                                            paymentSummary && paymentSummary?.payable_amount < 0 ? 'error' : 'primary'
                                        }
                                        style={{ minWidth: 140, textAlign: 'right' }}
                                    >
                                        {formatCurrencyValue(paymentSummary?.payable_amount ?? 0)}
                                    </Text>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {!isExport && campaign?.has_budget ? <div className="divider" id="maindiv" /> : null}

            <div className="explanation-block">
                {ableToPay && !profileDetail.is_billing_info_ready ? (
                    <ReminderAlert
                        form="warning"
                        width="100%"
                        title={t('campaign.billinfoReminder.title')}
                        link={{
                            label: t('campaign.billinfoReminder.button.fillBillInfo'),
                            to: teamFilter ? `/teams/${teamFilter}/business-details?redirectTo=${templatePath}` : '',
                        }}
                    />
                ) : null}
                <div className="icon-explanation">
                    {campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH && (
                        <Icon size="24px" name="checkCircle" color="positive" form="fill" />
                    )}
                    {campaign?.status === CAMPAIGN_STATUS_ENUM.DRAFT ||
                    campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_EDIT ||
                    campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ? (
                        <NewTrans i18nKey="campaign.template.step3.paymentConfirmation.explianationText">
                            <Text scale={isPhoneSize ? 'bodyS400' : 'bodyM400'} color={explanationTextColor}>
                                By sending this campaign into the next steps and for internal review, I am agreeing to
                                <a
                                    href="https://www.partipost.com/terms-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hyperlink"
                                >
                                    Partipost&apos;s Terms and Conditions
                                </a>
                                and{' '}
                                <a
                                    href="https://www.partipost.com/privacy-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hyperlink"
                                >
                                    Privacy Policy
                                </a>
                                . After the review, the internal reviewer will provide the final payment breakdown and
                                payment method for you to pay and launch campaign.
                            </Text>
                        </NewTrans>
                    ) : (
                        <Text scale={isPhoneSize ? 'bodyS400' : 'bodyM400'} color={explanationTextColor}>
                            {explanationText}
                        </Text>
                    )}
                </div>
            </div>

            {ableToPay && (
                <RectangleButton
                    type="button"
                    onClick={() => setShowPaymentModal(true)}
                    size="l"
                    label={t('button.payCampaign', { paymentAmount: paymentAmountDisplay })}
                    style={{ alignSelf: 'flex-start' }}
                    isDisabled={!profileDetail.is_billing_info_ready}
                />
            )}

            <EditTemplateRestricted>
                <div className="template-button-group">
                    <RectangleButton
                        type="button"
                        className="back-button"
                        onClick={onBack}
                        size="l"
                        form="outlined"
                        label={t('button.back')}
                    />
                    <RectangleButton
                        type="button"
                        onClick={() => onSubmit(isExport)}
                        size="l"
                        isDisabled={
                            paymentSummary &&
                            (paymentSummary.slot_number < 1 ||
                                paymentSummary.payable_amount < 0 ||
                                Boolean(budgetInputError))
                        }
                        label={t(
                            isExport
                                ? 'campaign.template.step3.paymentConfirmation.transferToCampaignEditor'
                                : 'campaign.template.step3.paymentConfirmation.sendReviewButton',
                        )}
                    />
                </div>
            </EditTemplateRestricted>

            <PredefinedModal
                onClose={() =>
                    setOpenModal({
                        campaign: campaign,
                        additionalFee: undefined,
                        serviceFee: undefined,
                        type: undefined,
                        paymentSummary: paymentSummary,
                        isTemplate: true,
                        tax: undefined,
                    })
                }
                {...openModal}
            />
        </PaymentConfirmationWrapper>
    )
}

export default PaymentConfirmation

const PaymentConfirmationWrapper = styled.div`
    padding-bottom: 24px;
    max-width: 894px;
    height: fit-content;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[600]};

    .payment-confirmation-title {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }

    .summary-content-wrapper {
        .summary-content {
            background: ${({ theme }) => theme.colors.surface.infoPastel};
            border-radius: 12px;
            padding: 24px;
            display: flex;
            flex-direction: column;
            gap: ${({ theme }) => theme.spacing[400]};

            @media screen and (max-width: 375px) {
                padding: 16px;
            }

            .estimation {
                display: flex;
                justify-content: space-between;
                @media screen and (max-width: 375px) {
                    flex-direction: column;
                    word-break: break-all;
                }
            }

            .breakdown-container {
                display: flex;
                flex-direction: column;
                gap: ${({ theme }) => theme.spacing[400]};

                .per-partiposter-payout {
                    padding-right: 12px;
                    height: 38px;
                }
            }

            .payable-amount {
                padding-right: 12px;
                display: flex;
                flex-direction: column;
                gap: ${({ theme }) => theme.spacing[100]};
            }

            .payable-amount-breakdown {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                gap: 10px;

                .tax-label {
                    display: flex;
                    align-items: center;
                    gap: ${({ theme }) => theme.spacing[100]};
                }

                @media screen and (max-width: 375px) {
                    justify-content: space-between;
                }
            }
        }
    }

    .flex-between {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
    }

    .accordion-content {
        background: ${({ theme }) => theme.colors.surface.primary};
        border-radius: 12px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};
    }

    .accordion-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[300]};

        @media screen and (max-width: 375px) {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    .breakdown-payout {
        min-width: 100px;
        text-align: right;

        @media screen and (max-width: 375px) {
            width: 100%;
        }
    }

    .divider {
        width: 100%;
        border-bottom: 1px solid ${({ theme }) => theme.colors.divider.primary};
    }

    .explanation-block {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }

    .icon-explanation {
        display: flex;
        flex-direction: row;
        gap: ${({ theme }) => theme.spacing[200]};
    }

    .hyperlink {
        text-decoration: none;
        color: ${({ theme }) => theme.colors.text.action};
        cursor: pointer;
    }

    .template-button-group {
        width: 100%;
        display: flex;
        gap: ${({ theme }) => theme.spacing[600]};
        padding: 12px 0px;
        box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.06);

        @media screen and (max-width: 375px) {
            gap: ${({ theme }) => theme.spacing[300]};
            .back-button {
                min-width: fit-content;
                flex: 1;
            }
        }
    }

    .breakdown-section {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};

        .breakdown-item {
            gap: ${({ theme }) => theme.spacing[300]};
        }
    }

    @media screen and (max-width: 1024px) {
        padding: 0px;
    }
`
const BreakdownTitle = styled(Text)`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};
`

const BreakdownItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-between;
    gap: ${({ theme }) => theme.spacing[300]};
    min-height: 38px;

    @media screen and (max-width: 375px) {
        flex-direction: column;
        align-items: flex-start;
    }

    .breakdown-payout {
        min-width: 100px;
        text-align: right;

        @media screen and (max-width: 375px) {
            width: 100%;
        }
    }

    .padded-list {
        padding-left: 24px;

        @media screen and (max-width: 375px) {
            padding-left: 0%;
        }
    }

    .breakdown-info {
        flex: 1;
        @media screen and (max-width: 375px) {
            width: 100%;
            word-break: break-all;
        }
    }

    .breakdown-task-title {
        @media screen and (max-width: 375px) {
            width: 100%;
        }
    }

    .breakdown-additional {
        display: flex;
        gap: ${({ theme }) => theme.spacing[100]};
        text-align: right;

        @media screen and (max-width: 375px) {
            width: 100%;
        }
    }
`
